/**
 * Severidad por hito para la organización (catálogo 3.9, Carlos 28 sep 2026).
 *
 * «Mantendría una tabla por hito con el nivel de severidad según afecte a
 * producto propio o de la competencia, editable por el usuario admin de cada
 * compañía. […] Esa tabla es la que se precarga; el usuario la edita por
 * combinación hito–relación y la edición prevalece.»
 *
 * Todos los usuarios de la org la ven (explica la columna de Cambios); solo el
 * admin la cambia. «No se dispara» = «n/a» en el Excel: el hito no sale para esa
 * relación, así que no se ofrece editarla.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useLang, useT } from "../i18n";
import { ESTRUCTURA_PDF, groupColor, useShowHitoCode } from "../lib/hitos";
import AlertTextsPanel from "../components/AlertTextsPanel";
import HitoSheet from "../components/HitoSheet";
import { ConfirmDialog } from "../components/ui";

type Sev = "A" | "D" | "I";
/** «Alerta crítica» (catálogo 4.4, contrato con Italfarmaco punto 7): independiente de la severidad. */
type Critico = { default: boolean; org: boolean | null; efectivo: boolean };
type Cell = { default: Sev; org: Sev | null; efectiva: Sev; aplica: boolean; critico?: Critico };
type Desenlace = { clave: string; nombre: string; nombre_en: string; propio: Cell; competencia: Cell };
type Row = {
  id: string; nombre: string; grupo: number; grupo_nombre: string; aplica_a: string; cubierto: boolean; activo?: boolean;
  propio: Cell; competencia: Cell; desenlaces?: Desenlace[];
};
type Resp = { editable: boolean; items: Row[] };
type Rel = "propio" | "competencia";

export default function SeverityPage() {
  const t = useT();
  const showCode = useShowHitoCode();
  const lang = useLang();
  const [data, setData] = useState<Resp | null>(null);
  const [q, setQ] = useState("");
  const [onlyCovered, setOnlyCovered] = useState(true);
  const [tab, setTab] = useState<"sev" | "txt">("sev");
  const [saved, setSaved] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [sheet, setSheet] = useState<string | null>(null);      // ficha del hito (Carlos, 20 oct)
  const [confirmRestore, setConfirmRestore] = useState(false);  // restaurar todo (Carlos, 20 oct)

  const load = () => api.get<Resp>("/hitos/severidad").then(setData).catch(e => setErr(String(e?.message ?? e)));
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data?.items ?? []).filter(r => (!onlyCovered || r.cubierto)
      && (!needle || r.id.toLowerCase().includes(needle) || r.nombre.toLowerCase().includes(needle)));
  }, [data, q, onlyCovered]);

  const save = async (row: Row, rel: Rel, sev: Sev | null, desenlace?: string) => {
    setErr(null);
    try {
      await api.put(`/hitos/${row.id}/severidad`, { relacion: rel, sev, desenlace: desenlace ?? null });
      setSaved(`${row.id}-${desenlace ?? ""}-${rel}`);
      setTimeout(() => setSaved(null), 1500);
      await load();
    } catch (e: unknown) {
      setErr(String((e as Error)?.message ?? e));
    }
  };

  const saveCritico = async (row: Row, rel: Rel, critico: boolean | null) => {
    setErr(null);
    try {
      await api.put(`/hitos/${row.id}/critico`, { relacion: rel, critico });
      setSaved(`${row.id}--${rel}`);
      setTimeout(() => setSaved(null), 1500);
      await load();
    } catch (e: unknown) {
      setErr(String((e as Error)?.message ?? e));
    }
  };

  // Cuántas severidades y marcas de crítico ha cambiado la organización (celdas y desenlaces).
  const changedCount = (data?.items ?? []).reduce((n, r) =>
    n + [r.propio, r.competencia, ...(r.desenlaces ?? []).flatMap(d => [d.propio, d.competencia])]
      .filter(c => c.org !== null).length
      + [r.propio, r.competencia].filter(c => c.critico && c.critico.org !== null).length, 0);
  const restoreAll = async () => {
    setConfirmRestore(false); setErr(null);
    try { await api.delete("/hitos/severidad"); await load(); } catch (e: unknown) { setErr(String((e as Error)?.message ?? e)); }
  };

  const sevLabel = (s: Sev) => t(`hitos.sev${s}`);
  const cell = (row: Row, rel: Rel, d?: Desenlace) => {
    const c = d ? d[rel] : row[rel];
    if (!c.aplica) return <span style={{ color: "var(--clr-text4)", fontSize: "0.75rem" }}>{t("hitos.sevNa")}</span>;
    const changed = c.org !== null;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {data?.editable ? (
          <select value={c.efectiva} onChange={e => save(row, rel, e.target.value as Sev, d?.clave)}
            style={{ fontSize: "0.78rem", padding: "4px 8px", borderRadius: 8, border: "1px solid var(--clr-border)",
                     background: "var(--clr-surface)", color: "var(--clr-text)", fontWeight: changed ? 700 : 400, maxWidth: 220 }}>
            {(["A", "D", "I"] as Sev[]).map(s => <option key={s} value={s}>{sevLabel(s)}</option>)}
          </select>
        ) : (
          <span style={{ fontSize: "0.8rem", fontWeight: changed ? 700 : 400 }}>{sevLabel(c.efectiva)}</span>
        )}
        {changed && (
          <span style={{ fontSize: "0.68rem", color: "var(--clr-text4)" }}>
            {t("hitos.sevDefault", { sev: sevLabel(c.default) })}
            {data?.editable && (
              <> · <button onClick={() => save(row, rel, null, d?.clave)}
                style={{ background: "none", border: 0, padding: 0, color: "var(--clr-org-primary)", cursor: "pointer", fontSize: "0.68rem" }}>
                {t("hitos.sevRestore")}</button></>
            )}
          </span>
        )}
        {!d && c.critico && (() => {
          const k = c.critico;
          const crit = (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "0.7rem", fontWeight: 600,
                           color: k.efectivo ? "#b91c1c" : "var(--clr-text4)" }}>
              {k.efectivo ? "🚨 " + t("hitos.critOn") : t("hitos.critOff")}
            </span>
          );
          return (
            <span style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
              {data?.editable ? (
                <label style={{ display: "inline-flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
                  <input type="checkbox" checked={k.efectivo} onChange={e => saveCritico(row, rel, e.target.checked)}
                    aria-label={t("hitos.critAria", { hito: row.nombre })} />
                  {crit}
                </label>
              ) : crit}
              {k.org !== null && (
                <span style={{ fontSize: "0.68rem", color: "var(--clr-text4)" }}>
                  {t(k.default ? "hitos.critDefaultOn" : "hitos.critDefaultOff")}
                  {data?.editable && (
                    <> · <button onClick={() => saveCritico(row, rel, null)}
                      style={{ background: "none", border: 0, padding: 0, color: "var(--clr-org-primary)", cursor: "pointer", fontSize: "0.68rem" }}>
                      {t("hitos.sevRestore")}</button></>
                  )}
                </span>
              )}
            </span>
          );
        })()}
        {saved === `${row.id}-${d?.clave ?? ""}-${rel}` && <span style={{ fontSize: "0.68rem", color: "#16803f" }}>{t("hitos.sevSaved")}</span>}
      </div>
    );
  };

  return (
    <div style={{ padding: "1.75rem 2rem", color: "var(--clr-text)", maxWidth: 1100 }}>
      <h1 style={{ margin: 0, fontSize: "1.4rem", fontWeight: 700, letterSpacing: "-0.02em" }}>{t("hitos.sevPageTitle")}</h1>
      <p style={{ margin: "6px 0 4px", fontSize: "0.84rem", color: "var(--clr-text3)", maxWidth: 820, lineHeight: 1.5 }}>
        {t("hitos.sevPageIntro")} <a href={ESTRUCTURA_PDF} target="_blank" rel="noreferrer" style={{ color: "var(--clr-org-primary)", fontWeight: 600, textDecoration: "none", whiteSpace: "nowrap" }}>{t("hitos.structurePdf")} ↗</a>
      </p>
      {data && !data.editable && (
        <p style={{ margin: "0 0 8px", fontSize: "0.78rem", color: "var(--clr-text4)" }}>{t("hitos.sevReadOnly")}</p>
      )}
      {/* Catálogo 4.1: los textos «Por qué importa» se ajustan aquí, como la severidad. */}
      <div role="tablist" style={{ display: "flex", gap: 4, margin: "14px 0 0", borderBottom: "1px solid var(--clr-border)" }}>
        {(["sev", "txt"] as const).map(k => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
            style={{ background: "none", border: 0, padding: "8px 14px", cursor: "pointer", fontSize: "0.84rem",
                     fontWeight: tab === k ? 700 : 500, color: tab === k ? "var(--clr-text)" : "var(--clr-text3)",
                     borderBottom: `2px solid ${tab === k ? "var(--clr-org-primary)" : "transparent"}`, marginBottom: -1 }}>
            {t(k === "sev" ? "hitos.tabSeverity" : "hitos.tabTexts")}
          </button>
        ))}
      </div>
      {tab === "txt" && (
        <p style={{ margin: "10px 0 0", fontSize: "0.8rem", color: "var(--clr-text3)", maxWidth: 820, lineHeight: 1.5 }}>
          {t("hitos.txtIntro")}
        </p>
      )}
      <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap", margin: "14px 0" }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={t("hitos.sevSearch")}
          style={{ fontSize: "0.82rem", padding: "7px 12px", borderRadius: 999, border: "1px solid var(--clr-border)",
                   background: "var(--clr-surface)", color: "var(--clr-text)", minWidth: 240 }} />
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.8rem", color: "var(--clr-text3)", cursor: "pointer" }}>
          <input type="checkbox" checked={onlyCovered} onChange={e => setOnlyCovered(e.target.checked)} />
          {t("hitos.sevOnlyCovered")}
        </label>
        {err && <span style={{ color: "#dc2626", fontSize: "0.8rem" }}>{err}</span>}
        {tab === "sev" && data?.editable && changedCount > 0 && (
          <button onClick={() => setConfirmRestore(true)}
            style={{ marginLeft: "auto", background: "transparent", border: "1px solid var(--clr-border)", borderRadius: 999,
                     padding: "6px 14px", fontSize: "0.78rem", fontWeight: 600, color: "var(--clr-text2)", cursor: "pointer" }}>
            {t("hitos.sevRestoreAll", { n: changedCount })}
          </button>
        )}
      </div>
      <ConfirmDialog open={confirmRestore} title={t("hitos.sevRestoreAllTitle")} message={t("hitos.sevRestoreAllMsg", { n: changedCount })}
        confirmLabel={t("hitos.restoreConfirm")} danger={false} onConfirm={restoreAll} onCancel={() => setConfirmRestore(false)} />
      {tab === "txt" ? <AlertTextsPanel q={q} onlyCovered={onlyCovered} /> : (
      <div style={{ background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 18, overflow: "hidden",
                    boxShadow: "var(--clr-shadow-sm)" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
            <thead>
              <tr style={{ textAlign: "left" }}>
                {[t("hitos.colHito"), t("hitos.sevColPropio"), t("hitos.sevColCompetencia")].map(h => (
                  <th key={h} style={{ padding: "0.7rem 12px", fontSize: "0.65rem", fontWeight: 600, textTransform: "uppercase",
                                       letterSpacing: 0.9, color: "var(--clr-text4)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ borderTop: "1px solid var(--clr-border2)" }}>
                  <td style={{ padding: "9px 12px", maxWidth: 480 }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      {showCode ? (
                        <span style={{ background: groupColor(r.grupo) + "1c", color: groupColor(r.grupo), padding: "2px 7px", borderRadius: 999,
                                       fontSize: "0.7rem", fontWeight: 700, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>{r.id}</span>
                      ) : (
                        <span aria-hidden style={{ width: 8, height: 8, borderRadius: 999, background: groupColor(r.grupo), flex: "none" }} />
                      )}
                      <button onClick={() => setSheet(r.id)} title={t("hitos.sheetOpen")}
                        style={{ background: "none", border: 0, padding: 0, cursor: "pointer", color: "var(--clr-text)", fontSize: "inherit",
                                 fontFamily: "inherit", textAlign: "left", textDecoration: "underline dotted", textUnderlineOffset: 3 }}>
                        {r.nombre}
                      </button>
                    </span>
                    {!r.cubierto && (
                      <div style={{ fontSize: "0.68rem", color: "var(--clr-text4)", marginTop: 2 }}>{t(r.activo === false ? "hitos.inactivo" : "hitos.sinCobertura")}</div>
                    )}
                  </td>
                  <td style={{ padding: "9px 12px", verticalAlign: "top" }}>{cell(r, "propio")}</td>
                  <td style={{ padding: "9px 12px", verticalAlign: "top" }}>{cell(r, "competencia")}</td>
                </tr>
              )).flatMap((tr, i) => {
                const r = rows[i];
                if (!r.desenlaces?.length) return [tr];
                return [tr, ...r.desenlaces.map((d, j) => (
                  <tr key={`${r.id}-${d.clave}`} style={{ background: "var(--clr-surface2, transparent)" }}>
                    <td style={{ padding: "5px 12px 5px 40px", fontSize: "0.78rem", color: "var(--clr-text3)" }}>
                      {j === 0 && <div style={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: 0.6,
                                                color: "var(--clr-text4)", marginBottom: 2 }}>{t("hitos.sevDesenlaces")}</div>}
                      ↳ {lang === "en" ? d.nombre_en : d.nombre}
                    </td>
                    <td style={{ padding: "5px 12px", verticalAlign: "top" }}>{cell(r, "propio", d)}</td>
                    <td style={{ padding: "5px 12px", verticalAlign: "top" }}>{cell(r, "competencia", d)}</td>
                  </tr>
                ))];
              })}
            </tbody>
          </table>
        </div>
      </div>
      )}
      {sheet && <HitoSheet hitoId={sheet} onClose={() => setSheet(null)} />}
    </div>
  );
}
